import {
  Box,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import type { AttachmentDto } from "../../../../api/types";
import AttachmentPreview from "../../../../components/AttachmentPreview";
import { DateTimeDisplay } from "../../../../components/DateTimeDisplay";
import { AdminTileCard } from "../../../../components/AdminList";
import { formatSize } from "../../../../logic/format-size";

export type AttachmentTileProps = {
  attachment: AttachmentDto;
  onManage: (a: AttachmentDto) => void;
  onCreateInvoice: (a: AttachmentDto) => void;
};

export const AttachmentTile = ({
  attachment,
  onManage,
  onCreateInvoice,
}: AttachmentTileProps) => (
  <AdminTileCard
    footer={
      <>
        <Tooltip title="Create invoice">
          <IconButton
            size="small"
            aria-label={`Create invoice from ${attachment.filename}`}
            onClick={() => onCreateInvoice(attachment)}
          >
            <RequestQuoteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <IconButton
          size="small"
          aria-label={`Manage ${attachment.filename}`}
          onClick={() => onManage(attachment)}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </>
    }
  >
    <Box sx={{ alignSelf: "center" }}>
      <AttachmentPreview
        attachment={attachment}
        onCreateInvoice={() => onCreateInvoice(attachment)}
      />
    </Box>
    <Typography
      variant="body2"
      noWrap
      title={attachment.filename}
      sx={{ fontWeight: 500 }}
    >
      {attachment.filename}
    </Typography>
    <Stack
      direction="row"
      spacing={1}
      sx={{ flexWrap: "wrap", fontSize: 12, color: "text.secondary" }}
    >
      <Typography component="span" variant="caption">
        {attachment.contentType === "application/pdf" ? "PDF" : "Image"}
      </Typography>
      <Typography component="span" variant="caption">
        {formatSize(attachment.size)}
      </Typography>
    </Stack>
    <Typography variant="caption" color="text.secondary">
      <DateTimeDisplay date={attachment.createdAt} />
    </Typography>
    {(attachment.hasInvoice || attachment.hasTransaction) && (
      <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap" }}>
        {attachment.hasInvoice && (
          <Chip
            size="small"
            icon={<RequestQuoteIcon />}
            label="Invoice"
            color="primary"
            variant="outlined"
          />
        )}
        {attachment.hasTransaction && (
          <Chip
            size="small"
            icon={<ReceiptLongIcon />}
            label="Transaction"
            color="secondary"
            variant="outlined"
          />
        )}
      </Stack>
    )}
    {attachment.notes && (
      <Typography
        variant="caption"
        sx={{ fontStyle: "italic", color: "text.secondary" }}
      >
        {attachment.notes}
      </Typography>
    )}
  </AdminTileCard>
);
